import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Axios } from '../../axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { RejectLicensePayload } from '@utils/types/back-office/licensing';
import { ADMIN_LICENSE_REQUESTS_QUERY_KEY } from './use-get-admin-license-requests';

interface RejectLicenseParams {
  requestId: number;
  payload: RejectLicensePayload;
}

const rejectLicense = async ({
  requestId,
  payload
}: RejectLicenseParams): Promise<void> => {
  await Axios.post(`api/admin/licensing/requests/${requestId}/reject`, payload);
};

export const useRejectLicense = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError<BaseError>, RejectLicenseParams>(
    rejectLicense,
    {
      onSuccess: () => {
        toast.success(t('license-rejected-successfully'));
        queryClient.invalidateQueries([ADMIN_LICENSE_REQUESTS_QUERY_KEY]);
      },
      onError: (error: AxiosError<BaseError>) => {
        const data = error.response?.data;
        let errorMessage = t('failed-to-reject-license');

        if (data?.error_description) {
          errorMessage = data.error_description;
        } else if (data?.message) {
          errorMessage = data.message;
        } else if (data?.detail) {
          errorMessage = data.detail;
        }

        toast.error(errorMessage);
      }
    }
  );
};
